import { HttpClient } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { WS_ERROR, WS_LOG, WS_SEARCH_COMPLETED, WS_SUMMARIZE_RESPONSE, WS_SUMMARIZE_STATUS } from '@app/constants';
import { environment } from '@environments/environment';
import { Connector } from '@interfaces/connector';
import { initializeSearchResults, Result, SearchResults } from '@interfaces/result';
import { TranslateService } from '@ngx-translate/core';
import { getTranslation } from '@shared/translate-helper';
import * as CryptoJS from 'crypto-js';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { ApiService } from './api.service';
import { DecompressionService } from './decompression.service';
import { UtilsService } from './utils.service';

export interface ConnectorFilter extends Connector {
  name: string;
  checked?: boolean;
  image: string;
}

export interface FileType {
  type: string;
  checked?: boolean;
}

export interface Author {
  author: string;
  checked?: boolean;
}

export interface SortOption {
  label: string;
  value: string;
  checked: boolean;
}

export interface TypeCode {
  label: string;
  checked: boolean;
  value: string;
}

@Injectable({
  providedIn: 'root',
})
export class SearchService {
  public query = signal('');
  public showFilter = signal(false);
  public searchResults = signal(<SearchResults>{});
  public filteredResults = signal(<Result[]>[]);
  public searchResultsQuery = signal('');
  public searching = signal(false);
  public searchError = signal('');
  public searchCount = signal(0);
  public sourcesCount = signal(0);
  public searchComplete = signal(false);
  public connectors = signal(<ConnectorFilter[]>[]);
  public authors = signal(<Author[]>[]);
  public fileTypes = signal(<FileType[]>[]);
  public typeCodes = signal<TypeCode[]>([]);
  public connectorsCount = signal(0);
  public showFilters = signal(false);
  public scrollToFirstCard = signal(false);
  public spellChecked = signal(false);
  public spellCheckedQuery = signal({ original_query: '', spellcheck_query: '' });
  public showSearchFilters = signal(true);
  public showSearchLogs = signal(false);
  public triggerSearchAgain = signal(false);
  public noConnectors = signal(false);
  public searchId = signal('');

  public isHeaderSticky = false;
  public selectedDateRange = signal(<{ start: string; end: string }>{ start: '', end: '' });
  public presetDateFilters: { label: string; value: string }[] = [
    { label: '24hrs', value: 'day' },
    { label: 'Last week', value: 'week' },
    { label: 'Last Month', value: 'month' },
    { label: 'Last Year', value: 'year' },
  ];
  public selectedPresetDate = signal('');
  public appliedFilters = signal<{ label: string; count: number; filter_type: string }[]>([]);
  public searchLogs = signal<{ level: string; timestamp: string; message: string }[]>([]);
  public sortMethods = signal<{ default_sort: string; sort_methods: SortOption[] }>({
    default_sort: '',
    sort_methods: [],
  });
  public resultsIndices = signal<{ sort_method: string; result_ids: string[] }[]>([]);
  public selectedResultsForBulkAction = signal<Result[]>([]);

  constructor(
    private http: HttpClient,
    private apiService: ApiService,
    private translateService: TranslateService,
    private utilService: UtilsService,
    private decompressionService: DecompressionService,
    private spinner: NgxSpinnerService,
  ) {}

  public doSearch = (query: string, search_id: string, disableSpellCheck = false): Observable<any> => {
    return this.http.post(this.apiService.getSearchUrl(query), {
      q: query,
      disable_spellcheck: disableSpellCheck,
      room_id: search_id,
    });
  };

  public getWebsocketToken = (hubId: string): Observable<any> =>
    this.http.get(this.apiService.getSearchWebsocketTokenUrl(hubId));

  public getPastQueries = (): Observable<any> => this.http.get(this.apiService.getLastQueriesUrl());

  /**
   * Filters the search results based on the selected filters.
   * Updates the filteredResults and filterCount properties of the component.
   */
  public filterResults(scrollToFirstCard: boolean = true): void {
    const searchResults = this.searchResults();
    const connectorsFiltered = this.filterConnectors(searchResults.results);
    const typeFiltered = this.filterFileType(connectorsFiltered);
    const filteredByPresetDate = this.filterPresetDates(typeFiltered);
    const filterByDate = this.filterDate(filteredByPresetDate);
    const filterBySort = this.sortResults(filterByDate);
    const filterByTypeCodes = this.filterTypeCodes(filterBySort);
    this.filteredResults.set(filterByTypeCodes);

    // get all selected filters
    const appliedFilters: { label: string; count: number; filter_type: string }[] = [];
    this.connectors().forEach((connector) => {
      if (connector.checked) {
        appliedFilters.push({
          label: connector.name,
          count: this.getConnectorResultsCount(connector.name, false),
          filter_type: 'connectors',
        });
      }
    });
    this.fileTypes().forEach((fileType) => {
      if (fileType.checked) {
        appliedFilters.push({
          label: fileType.type,
          count: this.getCountForFileType(fileType.type),
          filter_type: 'result_type',
        });
      }
    });
    this.typeCodes().forEach((typeCode) => {
      if (typeCode.checked) {
        appliedFilters.push({
          label: typeCode.label,
          count: this.getCountForTypeCodeCount(typeCode.value, true),
          filter_type: 'type_code',
        });
      }
    });
    // check if date filter is applied
    if (this.selectedDateRange().start && this.selectedDateRange().end) {
      const startDate = this.utilService.formatDate(this.selectedDateRange().start, 'MMMM Do YYYY');
      const endDate = this.utilService.formatDate(this.selectedDateRange().end, 'MMMM Do YYYY');
      getTranslation(this.translateService, 'date_range').subscribe((t) => {
        appliedFilters.push({
          label: `${t}: ${startDate} to ${endDate}`,
          count: 1,
          filter_type: 'date',
        });
      });
    }

    if (this.selectedPresetDate()) {
      getTranslation(this.translateService, 'preset_date').subscribe((t) => {
        appliedFilters.push({
          label: `${t}: ${this.selectedPresetDate()}`,
          count: 1,
          filter_type: 'date',
        });
      });
    }

    this.appliedFilters.set(appliedFilters);
    if (scrollToFirstCard) this.scrollToFirstCard.set(true);
  }

  /* Encrypted search query  using crypto-js */
  public encryptSearchQuery(query: string): string {
    const encrypted = CryptoJS.AES.encrypt(query, environment.searchQueryEncryptionKey).toString();
    return encodeURIComponent(encrypted);
  }

  /* Decrypt search query using crypto-js */
  public decryptSearchQuery(encryptedParam: string): string {
    const toDecrypt = decodeURIComponent(encryptedParam);
    const bytes = CryptoJS.AES.decrypt(toDecrypt, environment.searchQueryEncryptionKey);
    return bytes.toString(CryptoJS.enc.Utf8);
  }

  /** Handles incoming WebSocket messages related to search.
   * @param message - The WebSocket message to handle.
   */
  public handleWebsocketMessage(message: any): void {
    const content =
      message.compression === 'gzip' ? this.decompressionService.decompressMessage(message.message) : message.message;

    if (message?.type === WS_SEARCH_COMPLETED) {
      this.parseFinalResults(content);
      this.searchComplete.set(true);
      this.stopSearch();
      this._publishLog('The search has been successfully completed. The results are being displayed.');
    } else if (message?.type === WS_ERROR) {
      this._publishLog(`Error: ${content}`);
      this.stopSearch();
    } else if (message?.type === WS_LOG) {
      const res = JSON.parse(content);
      const date = new Date(res.timestamp);
      this._publishLog(res.log, res.level, date);
    } else if (message?.type === WS_SUMMARIZE_RESPONSE || message?.type === WS_SUMMARIZE_STATUS) {
      this.setResultSummaryStatus(content);
    }
  }

  /**
   * Parses the final search results from the given data.
   * @param data - The data containing the final search results.
   */
  public parseFinalResults = (data: any): void => {
    if (data) {
      const res = JSON.parse(data);
      if (res.num_results > 0) {
        this.searchResults.set({ ...res });
        this.searchCount.set(Object.keys(res?.source_scores || {}).length);
        this.filterResults(false);
        this.resultsIndices.set(res.results_indices);
        this.getSortMethods(res);
        this.getResultDetails();
      } else {
        this.searchResults.set(initializeSearchResults());
        this.searchCount.set(0);
      }

      this.filterResults(false);
      this.searching.set(false);
      this.spinner.hide();
    }
  };

  /** Resets all search results and filters. */
  public resetAll(): void {
    this.authors.set([]);
    this.connectors.set([]);
    this.filteredResults.set([]);
    this.searchComplete.set(false);
    this.searching.set(true);
    this.searchLogs.set([]);
    this.searchResults.set(initializeSearchResults());
    this.sourcesCount.set(0);
    this.searching.set(true);
  }

  /** Parses the search result status data from the given message. */
  public setResultSummaryStatus(message: any): boolean {
    const dataJson = JSON.parse(message);
    const summary = dataJson?.summary || '';
    const summaryStatus = dataJson?.log || '';
    const resultId = dataJson?.result_id || false;
    if (!resultId) return false;
    let index = this.filteredResults().findIndex((e) => e.result_id == resultId);
    if (index >= 0) {
      this.filteredResults()[index].summaryStatus = summaryStatus;
      if (summary) {
        this.filteredResults()[index].summary = summary;
        this.filteredResults()[index].summaryStatus = 'summarized';
      }
      return true;
    }
    return false;
  }

  public getFilterTypeCount = (filterType: string): number => {
    return this.appliedFilters().filter((f) => f.filter_type === filterType)?.length || 0;
  };

  /**
   * Toggle sort option and filter results based on the selected sort option.
   * @param sortOption - The sort option to toggle.
   */
  public toggleSortOption(sortOption: SortOption): void {
    const sortMethods = this.sortMethods();

    sortMethods.sort_methods.forEach((so) => {
      so.checked = so.value === sortOption.value;
    });

    this.filterResults();
  }

  public getSelectedSortOption = (): SortOption | undefined =>
    this.sortMethods().sort_methods.find((option) => option.checked);

  public callConnectorResultAction = (action: string, searchresults: SearchResults): Observable<any> =>
    this.http.post(this.apiService.resultActionUrl(searchresults.search_id), { searchresults, action });

  /**
   * Get the search results from the server.
   * @param searchId - The search id to get the results for.
   */
  public exportResults = (searchId: string): Observable<any> =>
    this.http.get(this.apiService.getExportResultsUrl(searchId), { responseType: 'text' });

  public setSearchId(): void {
    const uniqueSearchId = this.getUniqueSearchId();
    this.searchId.set(uniqueSearchId);
  }

  /**
   * Returns the number of results for a given connector.
   * @param Connector - The connector to get the results count for.
   * @param showAllCount - If true, returns the total number of results for the given connector. If false, returns the number of results for the given connector after filtering.
   */
  public getConnectorResultsCount = (connector: string, showAllCount = false): number => {
    if (this.filtersCount > 0 && !showAllCount) {
      return this.filteredResults().filter((res) => res.companyconnector_name === connector).length;
    }

    return this.searchResults().results.filter((res) => res.companyconnector_name === connector).length;
  };

  /**
   * Returns the number of results for a given author.
   * @param author
   */
  public getCountForFileType(type: string, useFilteredResults = false): number {
    const results = useFilteredResults ? this.filteredResults() : this.searchResults().results;
    return results.filter((res) => res.type === type).length;
  }

  public getCountForTypeCodeCount = (typeCode: string, useFilteredResults = false): number => {
    const results = useFilteredResults ? this.filteredResults() : this.searchResults().results;
    return results.filter((res) => res.type_code === typeCode).length;
  };

  /**
   * Resets the state of all connectors to unchecked and filters the results.
   */
  public resetConnectors(): void {
    this.connectors().forEach((cn) => (cn.checked = false));
    this.filterResults();
  }

  /**
   * Resets all filters and selected date ranges in the search results component.
   */
  public resetFilters(): void {
    this.connectors.update((searchConnectors: ConnectorFilter[]) =>
      searchConnectors.map((cn) => ({ ...cn, checked: false })),
    );

    this.authors.update((searchAuthors: Author[]) => searchAuthors.map((aut: Author) => ({ ...aut, checked: false })));

    this.fileTypes.update((searchFileTypes: FileType[]) =>
      searchFileTypes.map((ft: FileType) => ({ ...ft, checked: false })),
    );

    this.typeCodes.update((typeCodes: TypeCode[]) => typeCodes.map((tc: TypeCode) => ({ ...tc, checked: false })));

    this.selectedDateRange.set({ start: '', end: '' });

    // Get default sort option
    this.sortMethods().sort_methods = this.sortMethods().sort_methods.map((so) => ({
      ...so,
      checked: so.value.includes(this.sortMethods().default_sort + '_asc'),
    }));

    this.selectedPresetDate.set('');
  }

  /**
   * Returns the total count of selected filters, including connectors, authors, file types, and date range.
   * @returns The total count of selected filters.
   */
  public get filtersCount(): number {
    const dateFilter = this.selectedDateRange().start && this.selectedDateRange().end ? 1 : 0;
    const count =
      this.connectors().filter((cn) => cn.checked).length +
      this.authors().filter((author) => author.checked).length +
      this.fileTypes().filter((ft) => ft.checked).length +
      this.typeCodes().filter((tc) => tc.checked).length;
    const presetDateCount = this.selectedPresetDate().length ? 1 : 0;
    return count + dateFilter + presetDateCount;
  }

  /** Stops the search process and performs necessary cleanup tasks. */
  public stopSearch(): void {
    this.searching.set(false);
    this.spinner.hide();

    if (this.searchResults().results.length === 0) {
      this.searchResults.set(initializeSearchResults());
      this.searchCount.set(0);
    }
  }

  private filterDate(results: Result[]): Result[] {
    if (!this.selectedDateRange().start || !this.selectedDateRange().end) return results;
    const res = results.filter((rs) => {
      if (!rs.published_date) return false;
      return moment(rs.published_date).isBetween(this.selectedDateRange().start, this.selectedDateRange().end);
    });
    return [...res];
  }

  /**
   * Retrieves the details of the search results.
   * This includes the connectors, authors, file types, and type codes
   */
  private getResultDetails(): void {
    this.getConnectors();
    this.getFileTypes();
    this.getAuthors();
    this.getTypeCodes();
  }

  /**
   * Retrieves the connectors from the search service and updates the list of connectors.
   * Removes duplicates and adds new connectors that are not already in the list.
   */
  private getConnectors(): void {
    let connectors = this.searchResults().results.map((rs: Result) => ({
      name: rs.companyconnector_name,
      image: rs.favicon,
      checked: false,
    }));
    // Remove duplicates
    connectors = connectors.filter(
      (connector, index, self) => index === self.findIndex((c) => c.name === connector.name),
    );
    connectors.sort((a, b) => a.name.localeCompare(b.name));
    this.connectors.set(connectors as ConnectorFilter[]);
  }

  private getAuthors(): void {
    let authors = this.searchResults().results.map((rs: Result) => rs.authors);
    authors = [...authors.flat(1).filter((author) => author !== ' ' && author !== 'null')];
    authors = [...new Set(authors.filter(Boolean))];
    this.authors.set(authors.map((author) => ({ author, checked: false })));
  }

  private getFileTypes(): void {
    this.searchResults().results.forEach((rs) => {
      if (rs.type === null) {
        rs.files === null || !rs.files?.length
          ? (rs.type = '')
          : (rs.type = rs.files[0].standard_extension || rs.files[0].extension);
      }
    });

    let fileTypes = this.searchResults().results.map((rs: Result) => rs.type);

    fileTypes = [...new Set(fileTypes)].filter(Boolean);
    fileTypes.sort((a, b) => a.localeCompare(b));
    this.fileTypes.set(fileTypes.map((file) => ({ type: file, checked: false })));
  }

  private getTypeCodes(): void {
    let typeCodes = this.searchResults().results.map((rs: Result) => rs.type_code);
    typeCodes = [...new Set(typeCodes)].filter(Boolean);
    // Resolve type code labels asynchronously
    const typeKeys = typeCodes.map((tc) => 'type_' + tc);
    getTranslation(this.translateService, typeKeys).subscribe((t) => {
      this.typeCodes.set(
        typeCodes.map((typeCode) => ({
          value: typeCode,
          checked: false,
          label: t['type_' + typeCode] || typeCode,
        })),
      );
      this.typeCodes.update((codes) => codes.sort((a, b) => a.label.localeCompare(b.label)));
    });
  }

  /** Publishes a log message with the specified level and timestamp. */
  private _publishLog(log: string, level = 'info', assigned_timestamp = new Date()): void {
    const timestamp = assigned_timestamp || new Date();
    const localTime = timestamp.toLocaleString();
    const logObj: { timestamp: string; level: string; message: string } = { level, timestamp: localTime, message: log };
    this.searchLogs.update((logs) => [...logs, logObj]);
  }

  private getSortMethods(results: any): void {
    const sortMethods = results.sort_methods;
    const sortOptions: SortOption[] = [];

    this.sortMethods().default_sort = results.default_sort_method;

    sortMethods.forEach((sortMethod: string) => {
      sortOptions.push({
        value: sortMethod + (sortMethod === 'relevance' ? '_dsc' : '_asc'),
        checked: sortMethod === results.default_sort_method,
        label: sortMethod,
      });
      // skip descending sort for relevance
      if (sortMethod === 'relevance') return;
      sortOptions.push({
        value: sortMethod + '_dsc',
        checked: false,
        label: sortMethod + ' (descending)',
      });
    });
    // Translate sort option labels in bulk
    const sortKeys = sortOptions.map((so) => so.label);
    getTranslation(this.translateService, sortKeys).subscribe((t) => {
      this.sortMethods().sort_methods = sortOptions.map((so) => ({
        ...so,
        label: t[so.label] || so.label,
      }));
    });
  }

  /**
   * Sorts the search results based on the selected sort option.
   * @param results - The search results to sort.
   * @returns The sorted search results.
   */
  private sortResults(results: Result[]): Result[] {
    const sortOption = this.sortMethods().sort_methods.find((option) => option.checked);

    if (!sortOption) return results;

    const originalOption = sortOption?.value.split('_')[0];
    const resultsIds = this.resultsIndices().find((result) => result.sort_method === originalOption)?.result_ids;
    let sortedResults: Result[];

    if (sortOption?.value.includes('dsc')) {
      sortedResults = resultsIds?.map((id) => results.find((result) => result.result_id === id)) as Result[];
      // remove undefined values
      sortedResults = sortedResults.filter((result) => result);
    } else {
      // Reverse the array to get the results based on the selected sort option
      sortedResults = resultsIds?.map((id) => results.find((result) => result.result_id === id)).reverse() as Result[];
      // remove undefined values
      sortedResults = sortedResults.filter((result) => result);
    }

    if (originalOption === 'date') {
      sortedResults.sort((a, b) => new Date(a.published_date).getTime() - new Date(b.published_date).getTime());
      if (sortOption?.value.includes('dsc')) sortedResults.reverse();
    }
    return sortedResults;
  }

  /**
   * Filter results based on selected connector.
   * @param results
   */
  private filterConnectors(results: Result[]): Result[] {
    const checkConnectors = this.connectors()
      .filter((connector) => connector.checked)
      .map((cn) => cn.name);

    if (checkConnectors.length === 0) {
      return results;
    }

    return [...results.filter((res) => checkConnectors.includes(res.companyconnector_name))];
  }

  private filterFileType(results: Result[]): Result[] {
    const checkTypes = this.fileTypes()
      .filter((fileType) => fileType.checked)
      .map((ft) => ft.type);

    if (checkTypes.length === 0) {
      return results;
    }

    return [...results.filter((res) => checkTypes.includes(res.type))];
  }

  private filterTypeCodes(results: Result[]): Result[] {
    const checkTypeCodes = this.typeCodes()
      .filter((typeCode) => typeCode.checked)
      .map((tc) => tc.value);

    if (checkTypeCodes.length === 0) {
      return results;
    }

    return [...results.filter((res) => checkTypeCodes.includes(res.type_code))];
  }

  private filterPresetDates(results: Result[]): Result[] {
    const today = moment();
    let filteredPresetDateResults: Result[];
    switch (this.selectedPresetDate()) {
      case 'day': {
        filteredPresetDateResults = results.filter(
          (result) => moment(today).diff(result.published_date, 'hours') <= 24,
        );
        break;
      }
      case 'week': {
        const lastSevenDays = moment().subtract(7, 'days');
        filteredPresetDateResults = results.filter((result) =>
          moment(lastSevenDays).isSame(result.published_date, 'week'),
        );
        break;
      }
      case 'month': {
        const lastMonth = moment().subtract(30, 'days');
        filteredPresetDateResults = results.filter((result) =>
          moment(lastMonth).isSame(result.published_date, 'month'),
        );
        break;
      }
      case 'year': {
        const lastYear = moment(today).year() - 1;
        filteredPresetDateResults = results.filter((result) => moment(result.published_date).year() === lastYear);
        break;
      }
      default:
        filteredPresetDateResults = results;
    }
    return filteredPresetDateResults;
  }

  /** Generates a unique search ID. */
  private getUniqueSearchId(): string {
    const dateStr = Date.now().toString(36);
    const randomStr = Math.random().toString(36).substring(2, 8);
    return `${dateStr}${randomStr}`;
  }
}
